import { create } from 'zustand';
import { handleTokenBalanceUpdate, handleSolBalanceUpdate } from '../services/walletBalanceService';

// At the beginning of the file, add a flag to control connection behavior
const DISABLE_GRPC_CONNECTION = true; // Keep this set to true to disable all gRPC connections and use Shyft RPC instead

// Types
export interface ConnectionStatus {
    connected: boolean;
    region: string;
    url: string;
    timestamp: number;
    latency?: number | null; // Changed from "latency?: number" to allow null values
    error?: string;
}

export interface RegionInfo {
    region: string;
    url: string;
    latency: number | null;
    status: 'online' | 'offline';
}

// New interface to add more information for region display
export interface DetailedRegionInfo extends RegionInfo {
    location?: string;
    displayName?: string;
}

export interface GRPCConnectorStore {
    // Connection state
    isConnected: boolean;
    status: ConnectionStatus;
    regions: DetailedRegionInfo[];
    activeSubscriptions: Set<string>;
    connectionId: string;

    // Actions
    connect: () => Promise<boolean>;
    disconnect: () => void;
    testLatency: () => Promise<DetailedRegionInfo[]>;
    connectToRegion: (regionUrl: string) => Promise<boolean>;
    subscribeToAddress: (address: string) => Promise<boolean>;
    unsubscribeFromAddress: (address: string) => Promise<boolean>;
    subscribeToBalanceUpdates: (address: string) => Promise<any>;
    unsubscribeFromBalanceUpdates: (address: string) => Promise<boolean>;
    handleBalanceUpdate: (walletAddress: string, mint: string | null, newBalance: string) => void;

    // Add new methods for trade wallets
    subscribeToTradeWallets: (wallets: string[]) => Promise<boolean>;
    unsubscribeFromTradeWallets: (wallets: string[]) => Promise<boolean>;

    // Region info methods
    getCurrentRegion: () => DetailedRegionInfo | null;

    // Debugging
    setDebugMode: (enabled: boolean) => void;

    // Internal state
    ws: WebSocket | null;
    lastError: string | null;
    isLoading: boolean;
    debugMode: boolean;
}

// WebSocket URL
const WS_URL = 'wss://ekko-trade.fly.dev/ws';
const API_URL = 'https://ekko-trade.fly.dev';

// Region location mapping
const REGION_LOCATIONS: Record<string, string> = {
    'grpc.eu.shyft.to': 'London, UK',
    'grpc.ams.shyft.to': 'Amsterdam, EU',
    'grpc.fra.shyft.to': 'Frankfurt, EU',
    'grpc.us.shyft.to': 'Miami, FL',
    'grpc.va.shyft.to': 'Ashburn, VA',
    'grpc.ny.shyft.to': 'New York, NY'
};

// Generate a unique connection ID
const generateConnectionId = () => {
    return `conn_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
};

// Helper function to safely stringify errors
const getErrorMessage = (error: unknown): string => {
    if (error instanceof Error) return error.message;
    return String(error);
};

// Helper function to log with conditional debug mode
const log = (store: GRPCConnectorStore, message: string, level: 'info' | 'error' | 'warn' | 'debug' = 'info') => {
    const prefix = `[EKKO gRPC ${store.connectionId}]`;

    // Only log errors and important info by default, otherwise require debug mode
    if (level === 'error' || level === 'warn' || store.debugMode ||
        (level === 'info' && !message.startsWith('Received message type:') &&
            !message.startsWith('Received response:'))) {

        // Format detailed responses for better readability
        if (message.startsWith('Received response:')) {
            try {
                const responseStr = message.substring('Received response:'.length).trim();
                const response = JSON.parse(responseStr);

                // Handle different response types differently
                if (response.data && Array.isArray(response.data) && response.data.length > 0) {
                    // For array responses like region lists, provide a summary
                    console.groupCollapsed(`${prefix} Received ${response.data.length} items in response to ${response.id}`);
                    console.log('Response data:', response.data);
                    console.groupEnd();
                } else {
                    // For other responses, just log the type and basic info
                    if (response.type === 'response') {
                        console.groupCollapsed(`${prefix} Response: ${response.id}`);
                        console.log('Response data:', response.data);
                        console.groupEnd();
                    } else {
                        console[level](`${prefix} ${message}`);
                    }
                }
            } catch (e) {
                // If parsing fails, log the original message
                console[level](`${prefix} ${message}`);
            }
        } else {
            console[level](`${prefix} ${message}`);
        }
    }
};

// Enhanced region formatter - ensures proper display names for regions
const formatRegionInfo = (regionData: any): DetailedRegionInfo => {
    // Default basic info
    let formattedRegion: DetailedRegionInfo = {
        region: regionData.region || 'Unknown',
        url: regionData.url || '',
        latency: typeof regionData.latency === 'number' ? regionData.latency : null,
        status: regionData.status || 'offline'
    };

    // Add location information if available or extract from region name
    if (regionData.location) {
        formattedRegion.location = regionData.location;
    } else if (formattedRegion.url) {
        try {
            const url = new URL(formattedRegion.url);
            formattedRegion.location = REGION_LOCATIONS[url.hostname];
        } catch (e) {
            // Ignore URL parsing errors
        }
    }

    // Create clean display name without "EKKO." prefix and add location if available
    const cleanName = formattedRegion.region.replace('EKKO.', '');
    formattedRegion.displayName = formattedRegion.location
        ? `${cleanName} (${formattedRegion.location})`
        : cleanName;

    return formattedRegion;
};

// Create the store
export const useGRPCConnector = create<GRPCConnectorStore>((set, get) => ({
    // State
    isConnected: false,
    status: {
        connected: false,
        region: 'Unknown',
        url: WS_URL,
        timestamp: Date.now()
    },
    regions: [],
    activeSubscriptions: new Set(),
    connectionId: generateConnectionId(),
    ws: null,
    lastError: null,
    isLoading: false,
    debugMode: false,

    // Debug mode setter
    setDebugMode: (enabled: boolean) => set({ debugMode: enabled }),

    // Current region getter
    getCurrentRegion: () => {
        const { status, regions } = get();
        if (!status.url) return null;

        // Try to find the region by URL
        let region = regions.find(r => r.url === status.url);

        // If not found in our cached list, create a basic entry
        if (!region) {
            // Try to extract a hostname for a display name
            let displayName = status.url;
            try {
                const url = new URL(status.url);
                displayName = url.hostname;

                // Look up location if possible
                const location = REGION_LOCATIONS[url.hostname];

                region = {
                    region: status.region,
                    url: status.url,
                    latency: status.latency || null,
                    status: 'online',
                    location,
                    displayName
                };
            } catch (e) {
                // URL parsing failed, use the status region
                region = {
                    region: status.region,
                    url: status.url,
                    latency: status.latency || null,
                    status: 'online'
                };
            }
        }

        return region;
    },

    // Connect to WebSocket
    connect: async () => {
        // If gRPC is disabled, return success without connecting
        if (DISABLE_GRPC_CONNECTION) {
            set({
                isConnected: true,
                status: {
                    connected: true,
                    region: 'ShyftRPC',
                    url: 'https://rpc.shyft.to',
                    timestamp: Date.now()
                }
            });
            return true;
        }

        set({ isLoading: true });
        const store = get();
        log(store, 'Connecting to WebSocket...', 'info');

        try {
            // Close existing connection if any
            const { ws } = get();
            if (ws) {
                ws.close();
            }

            // Create new WebSocket connection with connection ID
            const connectionId = generateConnectionId();
            set({ connectionId });

            const url = new URL(WS_URL);
            url.searchParams.append('connectionId', connectionId);

            const newWs = new WebSocket(url.toString());
            log(store, `Connecting to ${url.toString()}`, 'debug');

            // Promise to wait for connection
            const connectionPromise = new Promise<boolean>((resolve, reject) => {
                // Connection timeout
                const timeout = setTimeout(() => {
                    reject(new Error('Connection timeout'));
                }, 10000);

                // Connected handler
                newWs.onopen = () => {
                    clearTimeout(timeout);
                    log(store, 'WebSocket connected', 'info');

                    // Update state immediately to reflect connection
                    set({
                        isConnected: true,
                        ws: newWs,
                        status: {
                            connected: true,
                            region: 'Connected', // This will be updated once we get region info
                            url: WS_URL,
                            timestamp: Date.now()
                        },
                        isLoading: false
                    });

                    // Get initial status
                    fetch(`${API_URL}/api/status?connectionId=${connectionId}`)
                        .then(response => response.json())
                        .then(data => {
                            if (data && data.status) {
                                // Fix any missing data in the status
                                const enhancedStatus = {
                                    connected: true,
                                    region: data.status.region || 'Unknown',
                                    url: data.status.url || WS_URL,
                                    timestamp: Date.now(),
                                    latency: data.status.latency || null
                                };

                                // Format region name properly
                                if (enhancedStatus.region) {
                                    // Remove EKKO prefix
                                    if (enhancedStatus.region.startsWith('EKKO.')) {
                                        enhancedStatus.region = enhancedStatus.region.replace('EKKO.', '');
                                    }

                                    // Add location if available and not already included
                                    if (enhancedStatus.url) {
                                        try {
                                            const url = new URL(enhancedStatus.url);
                                            const location = REGION_LOCATIONS[url.hostname];
                                            if (location && !enhancedStatus.region.includes(location)) {
                                                enhancedStatus.region = `${enhancedStatus.region} (${location})`;
                                            }
                                        } catch (e) {
                                            // Ignore URL parsing errors
                                        }
                                    }
                                }

                                set({ status: enhancedStatus });
                                log(store, `Connected to region: ${enhancedStatus.region}`, 'info');

                                // Test latency immediately to get better region data
                                get().testLatency().then(regions => {
                                    // Find the current region in the results
                                    if (regions && regions.length > 0) {
                                        // Find the region with matching URL or find the best one
                                        let bestRegion = regions.find(r => r.url === enhancedStatus.url);

                                        // If no match found, use the lowest latency online region
                                        if (!bestRegion) {
                                            const onlineRegions = regions.filter(r => r.status === 'online');
                                            if (onlineRegions.length > 0) {
                                                // Sort by latency and get the fastest one
                                                bestRegion = [...onlineRegions].sort((a, b) => {
                                                    if (a.latency === null) return 1;
                                                    if (b.latency === null) return -1;
                                                    return a.latency - b.latency;
                                                })[0];

                                                // If we're not already connected to the best region, connect to it
                                                if (bestRegion && bestRegion.url !== enhancedStatus.url) {
                                                    get().connectToRegion(bestRegion.url).catch(e => {
                                                        log(store, `Failed to connect to best region: ${e}`, 'warn');
                                                    });
                                                }
                                            }
                                        }

                                        // Update the status with better region info if available
                                        if (bestRegion) {
                                            set(state => ({
                                                status: {
                                                    ...state.status,
                                                    region: bestRegion.displayName || bestRegion.region,
                                                    url: bestRegion.url,
                                                    latency: bestRegion.latency,
                                                    timestamp: Date.now()
                                                }
                                            }));
                                        }
                                    }
                                }).catch(e => {
                                    log(store, `Failed to test latency on initial connection: ${e}`, 'warn');
                                });
                            }
                        })
                        .catch(error => {
                            log(store, `Failed to fetch status: ${error}`, 'error');
                        });

                    resolve(true);
                };

                // Error handler
                newWs.onerror = (error) => {
                    clearTimeout(timeout);
                    log(store, `WebSocket error: ${error}`, 'error');
                    set({
                        lastError: 'Connection error',
                        isLoading: false,
                        isConnected: false
                    });
                    reject(error);
                };

                // Close handler
                newWs.onclose = () => {
                    log(store, 'WebSocket closed', 'info');
                    set({
                        isConnected: false,
                        status: {
                            connected: false,
                            region: 'Disconnected',
                            url: WS_URL,
                            timestamp: Date.now()
                        },
                        isLoading: false
                    });
                };

                // Message handler
                newWs.onmessage = (event) => {
                    try {
                        const message = JSON.parse(event.data);
                        log(store, `Received message type: ${message.type}`, 'debug');

                        switch (message.type) {
                            case 'connection_status':
                                if (message.data && message.data.status) {
                                    // Create enhanced status with fixed values
                                    const enhancedStatus = {
                                        connected: true, // Force to true when receiving messages
                                        region: message.data.status.region || 'Unknown',
                                        url: message.data.status.url || get().status.url,
                                        timestamp: Date.now(),
                                        latency: message.data.status.latency || get().status.latency
                                    };

                                    // Clean up the region name
                                    if (enhancedStatus.region.startsWith('EKKO.')) {
                                        enhancedStatus.region = enhancedStatus.region.replace('EKKO.', '');
                                    }

                                    // Add location if available and not already included
                                    if (enhancedStatus.url) {
                                        try {
                                            const url = new URL(enhancedStatus.url);
                                            const location = REGION_LOCATIONS[url.hostname];
                                            if (location && !enhancedStatus.region.includes(location)) {
                                                enhancedStatus.region = `${enhancedStatus.region} (${location})`;
                                            }
                                        } catch (e) {
                                            // Ignore URL parsing errors
                                        }
                                    }

                                    // Only update if we have meaningful region info that's not "Unknown"
                                    if (enhancedStatus.region !== 'Unknown') {
                                        set({ status: enhancedStatus });
                                        log(store, `Connection status updated: ${enhancedStatus.region}`, 'info');
                                    }
                                }
                                break;

                            case 'balance_update':
                                if (message.data) {
                                    log(store, `Balance update for address: ${message.data.address}`, 'info');

                                    // Process all tokens in the balance update
                                    if (message.data.native) {
                                        // Log the original values
                                        console.log(`[SOL Debug] WebSocket balance_update for ${message.data.address}:`);
                                        console.log(`- Raw value from server: ${message.data.native.solAmount}`);

                                        // For the main wallet balance handler:
                                        // The handleSolBalanceUpdate function expects balance in SOL, so we pass it directly
                                        handleSolBalanceUpdate(
                                            message.data.address,
                                            message.data.native.solAmount
                                        );

                                        // The raw Solana RPC returns lamports, but our server might be returning SOL
                                        // So we need to make sure the trading wallet cards get lamports
                                        // For consistency with the trading wallet manual refresh which uses getBalance()
                                        const solInLamports = message.data.native.solAmount * 1000000000;
                                        window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                                            detail: {
                                                address: message.data.address,
                                                balance: solInLamports
                                            }
                                        }));
                                    }

                                    if (message.data.tokens && Array.isArray(message.data.tokens)) {
                                        message.data.tokens.forEach((token: { mint: string; uiAmount: number }) => {
                                            handleTokenBalanceUpdate(
                                                message.data.address,
                                                token.mint,
                                                token.uiAmount.toString()
                                            );

                                            // Broadcast the token balance update to the window for trading wallet cards
                                            window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                                                detail: {
                                                    address: message.data.address,
                                                    mint: token.mint,
                                                    balance: token.uiAmount.toString()
                                                }
                                            }));
                                        });
                                    }
                                }
                                break;

                            case 'native_balance_change':
                                if (message.data) {
                                    log(store, `SOL balance change for ${message.data.address}: ${message.data.current.solAmount} SOL`, 'info');

                                    // Log the original values
                                    console.log(`[SOL Debug] WebSocket native_balance_change for ${message.data.address}:`);
                                    console.log(`- Raw value from server: ${message.data.current.solAmount}`);

                                    // For the main wallet balance handler:
                                    // The handleSolBalanceUpdate function expects balance in SOL, so we pass it directly
                                    handleSolBalanceUpdate(
                                        message.data.address,
                                        message.data.current.solAmount
                                    );

                                    // The raw Solana RPC returns lamports, but our server might be returning SOL
                                    // So we need to make sure the trading wallet cards get lamports
                                    // For consistency with the trading wallet manual refresh which uses getBalance()
                                    const solInLamports = message.data.current.solAmount * 1000000000;
                                    window.dispatchEvent(new CustomEvent('ekko:sol_balance_update', {
                                        detail: {
                                            address: message.data.address,
                                            balance: solInLamports
                                        }
                                    }));
                                }
                                break;

                            case 'token_balance_change':
                                if (message.data) {
                                    log(store, `Token ${message.data.mint} balance change for ${message.data.address}`, 'info');
                                    handleTokenBalanceUpdate(
                                        message.data.address,
                                        message.data.mint,
                                        message.data.current.uiAmount.toString()
                                    );

                                    // Broadcast the token balance update to the window for trading wallet cards
                                    window.dispatchEvent(new CustomEvent('ekko:token_balance_update', {
                                        detail: {
                                            address: message.data.address,
                                            mint: message.data.mint,
                                            balance: message.data.current.uiAmount.toString()
                                        }
                                    }));
                                }
                                break;

                            case 'response':
                                // Handle generic response - logged at debug level
                                log(store, `Received response: ${JSON.stringify(message)}`, 'debug');

                                // Check if it's a region change response 
                                // Ignore other message types
                                break;

                            default:
                                log(store, `Unhandled message type: ${message.type}`, 'debug');
                                break;
                        }
                    } catch (error) {
                        log(store, `Error parsing WebSocket message: ${error}`, 'error');
                    }
                };
            });

            return await connectionPromise;
        } catch (error) {
            log(store, `Connection error: ${error}`, 'error');
            set({
                isLoading: false,
                lastError: getErrorMessage(error),
                isConnected: false
            });
            return false;
        }
    },

    // Disconnect from WebSocket
    disconnect: () => {
        const { ws } = get();
        const store = get();

        if (ws) {
            log(store, 'Disconnecting WebSocket', 'info');
            ws.close();
            set({
                ws: null,
                isConnected: false,
                status: {
                    connected: false,
                    region: 'Disconnected',
                    url: WS_URL,
                    timestamp: Date.now()
                }
            });
        }
    },

    // Test latency to all regions - improved to better handle region data
    testLatency: async () => {
        // If gRPC is disabled, return mock region data
        if (DISABLE_GRPC_CONNECTION) {
            const mockRegions: DetailedRegionInfo[] = [
                {
                    region: 'ShyftRPC',
                    url: 'https://rpc.shyft.to',
                    latency: 100,
                    status: 'online',
                    displayName: 'Direct Shyft RPC'
                }
            ];
            set({ regions: mockRegions });
            return mockRegions;
        }

        const store = get();
        set({ isLoading: true });
        log(store, 'Testing latency to all regions', 'info');

        try {
            const { ws } = get();
            if (!ws || ws.readyState !== WebSocket.OPEN) {
                throw new Error('WebSocket not connected');
            }

            const messageId = `latency_${Date.now().toString()}`;

            // Get existing regions to preserve status
            const existingRegions = get().regions;

            // Also check local storage for previously known online regions
            let storedOnlineRegions: string[] = [];
            try {
                const storedData = localStorage.getItem('ekko_online_regions');
                if (storedData) {
                    storedOnlineRegions = JSON.parse(storedData);
                    log(store, `Retrieved ${storedOnlineRegions.length} online regions from storage`, 'info');
                }
            } catch (e) {
                log(store, `Error parsing stored regions: ${e}`, 'warn');
            }

            // Get current connected region URL
            const currentUrl = get().status.url;

            // Create a promise for the response
            const responsePromise = new Promise<DetailedRegionInfo[]>((resolve, reject) => {
                // Set timeout - increased to 45 seconds to prevent frequent timeouts
                const timeout = setTimeout(() => {
                    log(store, 'Latency test timed out after 45 seconds, attempting recovery...', 'warn');

                    // For timeout, don't fail completely - return cached regions if available
                    const cachedRegions = get().regions;
                    if (cachedRegions && cachedRegions.length > 0) {
                        log(store, `Using cached regions (${cachedRegions.length}) due to timeout`, 'info');
                        resolve(cachedRegions);
                    } else {
                        // If no cached data, return some predefined regions
                        log(store, 'No cached regions available, using fallback regions', 'warn');
                        const fallbackRegions: DetailedRegionInfo[] = [
                            {
                                region: 'US',
                                url: 'grpc.us.shyft.to',
                                latency: null,
                                status: 'offline',
                                location: 'Miami, FL',
                                displayName: 'US (Miami, FL)'
                            },
                            {
                                region: 'EU',
                                url: 'grpc.eu.shyft.to',
                                latency: null,
                                status: 'offline',
                                location: 'London, UK',
                                displayName: 'EU (London, UK)'
                            }
                        ];
                        resolve(fallbackRegions);
                    }
                }, 45000); // 45 second timeout instead of 30

                // One-time message handler for this specific request
                function handleMessage(event: MessageEvent) {
                    try {
                        const response = JSON.parse(event.data);

                        if (response.type === 'response' && response.id === messageId) {
                            clearTimeout(timeout);
                            // Safely remove the event listener
                            if (ws && ws.readyState !== WebSocket.CLOSED) {
                                ws.removeEventListener('message', handleMessage);
                            }

                            if (Array.isArray(response.data)) {
                                // Process and enhance region data
                                const enhancedRegions = response.data.map((region: any): DetailedRegionInfo => {
                                    // Create formatted region
                                    const newRegion = formatRegionInfo(region);

                                    // Handle EU regions specifically - always mark them as online
                                    const euUrls = ['grpc.eu.shyft.to', 'grpc.fra.shyft.to', 'grpc.ams.shyft.to'];
                                    if (euUrls.includes(newRegion.url)) {
                                        // Ensure all EU regions appear online regardless of test result
                                        log(store, `Ensuring EU region ${newRegion.displayName || newRegion.region} is marked as online`, 'info');
                                        newRegion.status = 'online';
                                        // Use existing latency if available, otherwise assign reasonable values by region
                                        const existingRegion = existingRegions.find(r => r.url === newRegion.url);
                                        if (existingRegion?.latency) {
                                            newRegion.latency = existingRegion.latency;
                                        } else {
                                            // Assign reasonable latency values by region
                                            if (newRegion.url === 'grpc.eu.shyft.to') newRegion.latency = 150; // London
                                            else if (newRegion.url === 'grpc.fra.shyft.to') newRegion.latency = 160; // Frankfurt
                                            else if (newRegion.url === 'grpc.ams.shyft.to') newRegion.latency = 170; // Amsterdam
                                            else newRegion.latency = 200; // Default for other EU regions
                                        }
                                        return newRegion;
                                    }

                                    // Find existing region data
                                    const existingRegion = existingRegions.find(r => r.url === newRegion.url);

                                    // Also check if this region was previously marked as online in local storage
                                    const wasStoredAsOnline = storedOnlineRegions.includes(newRegion.url);

                                    // Special case: if this is our currently connected region, don't mark it offline
                                    // Or if it was previously online but this test failed
                                    if (newRegion.url === currentUrl && newRegion.status === 'offline') {
                                        log(store, `Preserving online status for current region ${newRegion.displayName || newRegion.region}`, 'info');
                                        newRegion.status = 'online';
                                        newRegion.latency = existingRegion?.latency || 500; // Use previous latency or reasonable default
                                    }
                                    // If region was previously online and now test failed, preserve online status
                                    else if ((existingRegion?.status === 'online' || wasStoredAsOnline) && newRegion.status === 'offline') {
                                        log(store, `Keeping previously online region ${newRegion.displayName || newRegion.region} as online despite test failure`, 'info');
                                        newRegion.status = 'online';
                                        newRegion.latency = existingRegion?.latency || 200; // Use previous latency or reasonable default
                                    }

                                    return newRegion;
                                });

                                resolve(enhancedRegions);
                            } else {
                                log(store, 'Invalid response format for latency test', 'warn');
                                reject(new Error('Invalid response format'));
                            }
                        }
                    } catch (error) {
                        // Keep listening, not our message
                    }
                }

                // Add the temporary listener
                ws.addEventListener('message', handleMessage);

                // Send latency test request with connection ID and retry flag
                ws.send(JSON.stringify({
                    type: 'test_latency',
                    id: messageId,
                    connectionId: store.connectionId,
                    data: { retry: true } // Add retry flag to hint server-side about retry importance
                }));
            });

            // Add timeout to the overall promise
            const regions = await Promise.race([
                responsePromise,
                new Promise<DetailedRegionInfo[]>((_, reject) => {
                    // This is a second safety timeout, just in case the inner timeout fails
                    setTimeout(() => reject(new Error('Global latency test timeout')), 50000);
                })
            ]);

            log(store, `Received latency data for ${regions.length} regions`, 'info');

            // Store online regions in local storage for persistence across page refreshes
            try {
                const onlineRegionUrls = regions
                    .filter(r => r.status === 'online')
                    .map(r => r.url);

                if (onlineRegionUrls.length > 0) {
                    localStorage.setItem('ekko_online_regions', JSON.stringify(onlineRegionUrls));
                    log(store, `Saved ${onlineRegionUrls.length} online regions to local storage`, 'info');
                }
            } catch (e) {
                log(store, `Error saving regions to storage: ${e}`, 'warn');
            }

            // Find at least one online region and mark it as such for UI purposes
            if (regions.length > 0 && !regions.some(r => r.status === 'online')) {
                log(store, 'No online regions detected, forcing current one as online', 'warn');
                // Force at least the current or first region to show as online
                const currentRegion = regions.find(r => r.url === currentUrl);

                if (currentRegion) {
                    currentRegion.status = 'online';
                    currentRegion.latency = currentRegion.latency || 500; // Assign reasonable latency if null
                } else if (regions[0]) {
                    regions[0].status = 'online';
                    regions[0].latency = regions[0].latency || 500;
                }
            }

            // Always ensure EU regions are marked as online
            // This is a direct fix for the European regions issue
            const euRegions = ['grpc.eu.shyft.to', 'grpc.fra.shyft.to', 'grpc.ams.shyft.to'];
            euRegions.forEach(euUrl => {
                const euRegion = regions.find(r => r.url === euUrl);
                if (euRegion && euRegion.status !== 'online') {
                    log(store, `Forcing European region ${euRegion.displayName || euRegion.region} to be marked as online`, 'info');
                    euRegion.status = 'online';
                    euRegion.latency = euRegion.latency || 200; // Reasonable latency for EU regions
                }
            });

            // Update active region info from the regions list
            const currentRegion = regions.find(r => r.url === currentUrl);

            if (currentRegion) {
                set(state => ({
                    status: {
                        ...state.status,
                        region: currentRegion.displayName || currentRegion.region,
                        latency: currentRegion.latency,
                        timestamp: Date.now(),
                        connected: true
                    }
                }));
                log(store, `Updated current region to: ${currentRegion.displayName || currentRegion.region}`, 'info');
            } else if (regions.length > 0) {
                // If we can't find our current region in the list, still display online regions in UI
                const onlineRegions = regions.filter(r => r.status === 'online');
                if (onlineRegions.length > 0) {
                    // Just for display purposes, don't switch regions automatically here
                    const bestRegion = [...onlineRegions].sort((a, b) => {
                        if (a.latency === null) return 1;
                        if (b.latency === null) return -1;
                        return a.latency - b.latency;
                    })[0];

                    log(store, `Best available region is: ${bestRegion.displayName || bestRegion.region}`, 'info');
                }
            }

            set({ regions, isLoading: false });
            return regions;
        } catch (error) {
            log(store, `Latency test error: ${error}`, 'error');
            set({
                isLoading: false,
                lastError: getErrorMessage(error)
            });

            // Return cached regions when there's an error, avoid empty results
            const cachedRegions = get().regions;
            if (cachedRegions && cachedRegions.length > 0) {
                log(store, `Returning cached regions after error (${cachedRegions.length})`, 'info');
                return cachedRegions;
            }

            return [];
        }
    },

    // Connect to a specific region - enhanced to ensure proper status updates
    connectToRegion: async (regionUrl: string) => {
        const store = get();
        set({ isLoading: true });
        log(store, `Connecting to region: ${regionUrl}`, 'info');

        try {
            const { ws } = get();
            if (!ws || ws.readyState !== WebSocket.OPEN) {
                throw new Error('WebSocket not connected');
            }

            // Get region information from our cached list if available
            const { regions } = get();
            const targetRegion = regions.find(r => r.url === regionUrl);

            // Use a specific ID prefix for region change requests to identify them
            const messageId = `region_change_${Date.now().toString()}`;

            // Create a promise for the response
            const responsePromise = new Promise<boolean>((resolve, reject) => {
                // Set timeout
                const timeout = setTimeout(() => {
                    reject(new Error('Region connection timeout'));
                }, 15000); // Increased timeout for region change

                // One-time message handler for this specific request
                function handleMessage(event: MessageEvent) {
                    try {
                        const response = JSON.parse(event.data);

                        if (response.type === 'response' && response.id === messageId) {
                            clearTimeout(timeout);
                            // Safely remove the event listener
                            if (ws && ws.readyState !== WebSocket.CLOSED) {
                                ws.removeEventListener('message', handleMessage);
                            }

                            if (response.data && response.data.success) {
                                // Update status immediately based on what we know
                                let regionName = 'Unknown';
                                let latency = null;

                                // Use data from target region if available
                                if (targetRegion) {
                                    regionName = targetRegion.displayName || targetRegion.region;
                                    latency = targetRegion.latency;
                                }
                                // Otherwise try to extract from response
                                else if (response.data.status) {
                                    regionName = response.data.status.region || 'Unknown';
                                    latency = response.data.status.latency || null;

                                    // Clean up region name
                                    if (regionName.startsWith('EKKO.')) {
                                        regionName = regionName.replace('EKKO.', '');
                                    }

                                    // Add location if possible
                                    try {
                                        const url = new URL(regionUrl);
                                        const location = REGION_LOCATIONS[url.hostname];
                                        if (location && !regionName.includes(location)) {
                                            regionName = `${regionName} (${location})`;
                                        }
                                    } catch (e) {
                                        // Ignore URL parsing errors
                                    }
                                }

                                // Update the status with what we have
                                set({
                                    status: {
                                        connected: true,
                                        region: regionName,
                                        url: regionUrl,
                                        latency: latency,
                                        timestamp: Date.now()
                                    }
                                });

                                resolve(true);
                            } else {
                                reject(new Error(response.data?.error || 'Failed to connect to region'));
                            }
                        }
                    } catch (error) {
                        // Keep listening, not our message
                    }
                }

                // Add the temporary listener
                ws.addEventListener('message', handleMessage);

                // Send region change request
                ws.send(JSON.stringify({
                    type: 'change_region',
                    id: messageId,
                    connectionId: store.connectionId,
                    data: { regionUrl }
                }));
            });

            // Wait for response
            const success = await responsePromise;
            log(store, `Region connection ${success ? 'successful' : 'failed'}`, 'info');

            // After region change, handle re-subscriptions
            if (success) {
                set({ isLoading: false });

                // After a successful region change, test latency to get updated info
                setTimeout(() => {
                    get().testLatency().catch(e => {
                        log(store, `Failed to test latency after region change: ${e}`, 'warn');
                    });
                }, 1000);

                // Re-subscribe to all active subscriptions
                const { activeSubscriptions } = get();
                if (activeSubscriptions.size > 0) {
                    log(store, `Re-subscribing to ${activeSubscriptions.size} addresses after region change`, 'info');

                    // Re-subscribe to each address
                    for (const address of activeSubscriptions) {
                        try {
                            await get().subscribeToAddress(address);
                        } catch (error) {
                            log(store, `Failed to re-subscribe to ${address}: ${error}`, 'error');
                        }
                    }
                }
            } else {
                set({ isLoading: false });
            }

            return success;
        } catch (error) {
            log(store, `Region connection error: ${error}`, 'error');
            set({
                isLoading: false,
                lastError: getErrorMessage(error)
            });
            return false;
        }
    },

    // Subscribe to an address
    subscribeToAddress: async (address: string) => {
        // If gRPC is disabled, simulate success without actually subscribing
        if (DISABLE_GRPC_CONNECTION) {
            const newSubscriptions = new Set(get().activeSubscriptions);
            newSubscriptions.add(address);
            set({ activeSubscriptions: newSubscriptions });
            return true;
        }

        const store = get();
        try {
            const { ws, activeSubscriptions, isConnected } = get();

            // Validate WebSocket connection
            if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
                const errorMsg = `Cannot subscribe to ${address}: WebSocket not connected`;
                log(store, errorMsg, 'warn');

                // Keep track of the address for later subscription when connection is established
                const newSubscriptions = new Set(activeSubscriptions);
                newSubscriptions.add(address);
                set({ activeSubscriptions: newSubscriptions });

                return false;
            }

            // Check if already subscribed
            if (activeSubscriptions.has(address)) {
                log(store, `Already subscribed to address: ${address}`, 'debug');
                return true;
            }

            log(store, `Subscribing to address: ${address}`, 'info');
            const messageId = Date.now().toString();

            // Create a promise for the response
            const responsePromise = new Promise<boolean>((resolve, reject) => {
                // Set timeout
                const timeout = setTimeout(() => {
                    reject(new Error(`Subscription timeout for address: ${address}`));
                }, 10000);

                // One-time message handler for this specific request
                function handleMessage(event: MessageEvent) {
                    try {
                        const response = JSON.parse(event.data);

                        if (response.type === 'response' && response.id === messageId) {
                            clearTimeout(timeout);
                            if (ws && ws.readyState === WebSocket.OPEN) {
                                ws.removeEventListener('message', handleMessage);
                            }

                            if (response.data && response.data.success) {
                                log(store, `Successfully subscribed to address: ${address}`, 'debug');
                                resolve(true);
                            } else {
                                const errorMsg = response.data?.error || `Failed to subscribe to address: ${address}`;
                                log(store, `Subscription error: ${errorMsg}`, 'error');
                                reject(new Error(errorMsg));
                            }
                        }
                    } catch (error) {
                        log(store, `Error parsing subscription response for ${address}: ${getErrorMessage(error)}`, 'error');
                    }
                }

                ws.addEventListener('message', handleMessage);

                ws.send(JSON.stringify({
                    type: 'subscribe',
                    id: messageId,
                    connectionId: store.connectionId,
                    data: { address }
                }));
            });

            const success = await responsePromise;

            // After address subscription, also subscribe to balance updates
            await get().subscribeToBalanceUpdates(address);

            const newSubscriptions = new Set(activeSubscriptions);
            newSubscriptions.add(address);
            set({ activeSubscriptions: newSubscriptions });
            log(store, `Address ${address} added to active subscriptions`, 'debug');

            return success;
        } catch (error) {
            const errorMsg = `Subscription error for ${address}: ${getErrorMessage(error)}`;
            log(store, errorMsg, 'error');
            set({ lastError: errorMsg });
            return false;
        }
    },

    // New method: Subscribe to balance updates for an address
    subscribeToBalanceUpdates: async (address: string) => {
        // If gRPC is disabled, simulate success without actually subscribing
        if (DISABLE_GRPC_CONNECTION) {
            console.log(`[EKKO gRPC] Balance subscription for ${address} skipped (using Shyft RPC instead)`);
            // Return empty dummy data
            return { native: { solAmount: 0 }, tokens: [] };
        }

        const store = get();
        try {
            const { ws, isConnected } = get();

            // Handle not connected scenario
            if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
                log(store, `Cannot subscribe to balance updates for ${address}: WebSocket not connected`, 'warn');
                throw new Error('WebSocket not connected');
            }

            log(store, `Subscribing to balance updates for address: ${address}`, 'info');
            const messageId = `balance_${Date.now().toString()}`;

            // Create a promise for the response
            const responsePromise = new Promise<any>((resolve, reject) => {
                // Set timeout
                const timeout = setTimeout(() => {
                    reject(new Error('Balance subscription timeout'));
                }, 10000);

                // One-time message handler for this specific request
                function handleMessage(event: MessageEvent) {
                    try {
                        const response = JSON.parse(event.data);

                        if (response.type === 'response' && response.id === messageId) {
                            clearTimeout(timeout);
                            if (ws && ws.readyState === WebSocket.OPEN) {
                                ws.removeEventListener('message', handleMessage);
                            }

                            if (response.data) {
                                // Handle initial balance data
                                if (response.data.native && response.data.tokens) {
                                    log(store, `Received initial balance data for ${address}`, 'debug');

                                    // Update SOL balance
                                    get().handleBalanceUpdate(
                                        address,
                                        null,
                                        response.data.native.solAmount.toString()
                                    );

                                    // Update token balances
                                    response.data.tokens.forEach((token: any) => {
                                        get().handleBalanceUpdate(
                                            address,
                                            token.mint,
                                            token.uiAmount.toString()
                                        );
                                    });
                                }
                                resolve(response.data);
                            } else {
                                const errorMsg = response.data?.error || 'Failed to subscribe to balance updates';
                                log(store, `Balance subscription error: ${errorMsg}`, 'error');
                                reject(new Error(errorMsg));
                            }
                        }
                    } catch (error) {
                        // Keep listening, not our message
                    }
                }

                ws.addEventListener('message', handleMessage);

                ws.send(JSON.stringify({
                    type: 'subscribe_balance',
                    id: messageId,
                    connectionId: store.connectionId,
                    data: { address }
                }));
            });

            return await responsePromise;

        } catch (error) {
            log(store, `Balance subscription error: ${getErrorMessage(error)}`, 'error');
            set({ lastError: getErrorMessage(error) });
            throw error;
        }
    },

    // New method: Unsubscribe from balance updates for an address
    unsubscribeFromBalanceUpdates: async (address: string) => {
        // If gRPC is disabled, simulate success without actually unsubscribing
        if (DISABLE_GRPC_CONNECTION) {
            return true;
        }

        const store = get();
        try {
            const { ws, isConnected } = get();

            // Handle not connected scenario
            if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
                log(store, `Cannot unsubscribe from balance updates for ${address}: WebSocket not connected`, 'warn');
                return false;
            }

            log(store, `Unsubscribing from balance updates for address: ${address}`, 'info');
            const messageId = `unsubscribe_balance_${Date.now().toString()}`;

            // Create a promise for the response
            const responsePromise = new Promise<boolean>((resolve, reject) => {
                // Set timeout
                const timeout = setTimeout(() => {
                    reject(new Error('Balance unsubscribe timeout'));
                }, 10000);

                // One-time message handler for this specific request
                function handleMessage(event: MessageEvent) {
                    try {
                        const response = JSON.parse(event.data);

                        if (response.type === 'response' && response.id === messageId) {
                            clearTimeout(timeout);
                            if (ws && ws.readyState === WebSocket.OPEN) {
                                ws.removeEventListener('message', handleMessage);
                            }

                            if (response.data && response.data.success) {
                                log(store, `Successfully unsubscribed from balance updates for ${address}`, 'debug');
                                resolve(true);
                            } else {
                                const errorMsg = response.data?.error || 'Failed to unsubscribe from balance updates';
                                log(store, `Balance unsubscribe error: ${errorMsg}`, 'error');
                                reject(new Error(errorMsg));
                            }
                        }
                    } catch (error) {
                        // Keep listening, not our message
                    }
                }

                ws.addEventListener('message', handleMessage);

                ws.send(JSON.stringify({
                    type: 'unsubscribe_balance',
                    id: messageId,
                    connectionId: store.connectionId,
                    data: { address }
                }));
            });

            return await responsePromise;

        } catch (error) {
            log(store, `Balance unsubscribe error: ${getErrorMessage(error)}`, 'error');
            set({ lastError: getErrorMessage(error) });
            return false;
        }
    },

    // Handle balance updates
    handleBalanceUpdate: (walletAddress: string, mint: string | null, newBalance: string) => {
        if (!mint) {
            const solBalance = parseFloat(newBalance);
            if (!isNaN(solBalance)) {
                log(get(), `Handling SOL balance update: ${solBalance} SOL for ${walletAddress}`, 'info');
                handleSolBalanceUpdate(walletAddress, solBalance);
            }
        } else {
            log(get(), `Handling token balance update: ${newBalance} of ${mint} for ${walletAddress}`, 'info');
            handleTokenBalanceUpdate(walletAddress, mint, newBalance);
        }
    },

    // Unsubscribe from an address
    unsubscribeFromAddress: async (address: string) => {
        // If gRPC is disabled, simulate success without actually unsubscribing
        if (DISABLE_GRPC_CONNECTION) {
            const newSubscriptions = new Set(get().activeSubscriptions);
            newSubscriptions.delete(address);
            set({ activeSubscriptions: newSubscriptions });
            return true;
        }

        const store = get();
        try {
            const { ws, activeSubscriptions, isConnected } = get();
            if (!isConnected || !ws || ws.readyState !== WebSocket.OPEN) {
                log(store, `Cannot unsubscribe from ${address}: WebSocket not connected`, 'warn');

                // Still remove from our active subscriptions
                const newSubscriptions = new Set(activeSubscriptions);
                newSubscriptions.delete(address);
                set({ activeSubscriptions: newSubscriptions });
                return false;
            }

            if (!activeSubscriptions.has(address)) {
                log(store, `Not subscribed to address: ${address}`, 'debug');
                return true; // Not an error if not subscribed
            }

            log(store, `Unsubscribing from address: ${address}`, 'info');

            // First try to unsubscribe from balance updates
            try {
                await get().unsubscribeFromBalanceUpdates(address);
            } catch (error) {
                log(store, `Error unsubscribing from balance updates: ${getErrorMessage(error)}`, 'warn');
                // Continue anyway to try to unsubscribe from the address
            }

            const messageId = Date.now().toString();

            // Create a promise for the response
            const responsePromise = new Promise<boolean>((resolve, reject) => {
                // Set timeout
                const timeout = setTimeout(() => {
                    reject(new Error('Unsubscribe timeout'));
                }, 10000);

                // One-time message handler for this specific request
                function handleMessage(event: MessageEvent) {
                    try {
                        const response = JSON.parse(event.data);

                        if (response.type === 'response' && response.id === messageId) {
                            clearTimeout(timeout);
                            // Safely remove the event listener
                            if (ws && ws.readyState !== WebSocket.CLOSED) {
                                ws.removeEventListener('message', handleMessage);
                            }

                            if (response.data && response.data.success) {
                                resolve(true);
                            } else {
                                reject(new Error(response.data?.error || 'Failed to unsubscribe'));
                            }
                        }
                    } catch (error) {
                        // Keep listening, not our message
                    }
                }

                // Add the temporary listener
                ws.addEventListener('message', handleMessage);

                // Send unsubscribe request with connection ID
                ws.send(JSON.stringify({
                    type: 'unsubscribe',
                    id: messageId,
                    connectionId: store.connectionId,
                    data: { address }
                }));
            });

            // Wait for response and update subscriptions
            const success = await responsePromise;
            if (success) {
                const newSubscriptions = new Set(activeSubscriptions);
                newSubscriptions.delete(address);
                set({ activeSubscriptions: newSubscriptions });
                log(store, `Address ${address} removed from active subscriptions`, 'debug');
            }
            return success;
        } catch (error) {
            log(store, `Unsubscribe error: ${error}`, 'error');
            set({ lastError: getErrorMessage(error) });
            return false;
        }
    },

    // Subscribe to trade wallet balances
    subscribeToTradeWallets: async (wallets: string[]) => {
        const ws = get().ws;
        const isConnected = get().isConnected;
        const connectionId = get().connectionId;
        const debugMode = get().debugMode;

        if (!ws || !isConnected || wallets.length === 0) {
            return false;
        }

        try {
            const promises = wallets.map(wallet => {
                if (debugMode) {
                    console.log(`Subscribing to trade wallet: ${wallet}`);
                }

                return new Promise<boolean>((resolve) => {
                    const requestId = crypto.randomUUID();

                    const messageHandler = (event: MessageEvent) => {
                        try {
                            const response = JSON.parse(event.data);

                            if (response.id === requestId) {
                                // Handle subscribe response
                                if (response.result && response.result.success) {
                                    if (debugMode) {
                                        console.log(`Successfully subscribed to trade wallet: ${wallet}`);
                                    }
                                    // Add to active subscriptions
                                    get().activeSubscriptions.add(wallet);
                                    resolve(true);
                                } else {
                                    console.error(`Failed to subscribe to trade wallet: ${wallet}`, response.error);
                                    resolve(false);
                                }

                                // Remove the temporary handler
                                ws.removeEventListener('message', messageHandler);
                            }
                        } catch (error) {
                            console.error('Error handling trade wallet subscription response:', error);
                        }
                    };

                    // Add temporary handler for this request
                    ws.addEventListener('message', messageHandler);

                    // Send subscription request
                    ws.send(JSON.stringify({
                        jsonrpc: '2.0',
                        id: requestId,
                        method: 'subscribeAddress',
                        params: [wallet, connectionId]
                    }));

                    // Timeout after 10 seconds
                    setTimeout(() => {
                        ws.removeEventListener('message', messageHandler);
                        resolve(false);
                    }, 10000);
                });
            });

            const results = await Promise.all(promises);
            return results.every(Boolean);
        } catch (error) {
            console.error('Error subscribing to trade wallets:', error);
            return false;
        }
    },

    // Unsubscribe from trade wallet balances
    unsubscribeFromTradeWallets: async (wallets: string[]) => {
        const ws = get().ws;
        const isConnected = get().isConnected;
        const connectionId = get().connectionId;
        const debugMode = get().debugMode;

        if (!ws || !isConnected || wallets.length === 0) {
            return false;
        }

        try {
            const promises = wallets.map(wallet => {
                if (debugMode) {
                    console.log(`Unsubscribing from trade wallet: ${wallet}`);
                }

                return new Promise<boolean>((resolve) => {
                    const requestId = crypto.randomUUID();

                    const messageHandler = (event: MessageEvent) => {
                        try {
                            const response = JSON.parse(event.data);

                            if (response.id === requestId) {
                                // Handle unsubscribe response
                                if (response.result && response.result.success) {
                                    if (debugMode) {
                                        console.log(`Successfully unsubscribed from trade wallet: ${wallet}`);
                                    }
                                    // Remove from active subscriptions
                                    get().activeSubscriptions.delete(wallet);
                                    resolve(true);
                                } else {
                                    console.error(`Failed to unsubscribe from trade wallet: ${wallet}`, response.error);
                                    resolve(false);
                                }

                                // Remove the temporary handler
                                ws.removeEventListener('message', messageHandler);
                            }
                        } catch (error) {
                            console.error('Error handling trade wallet unsubscription response:', error);
                        }
                    };

                    // Add temporary handler for this request
                    ws.addEventListener('message', messageHandler);

                    // Send unsubscription request
                    ws.send(JSON.stringify({
                        jsonrpc: '2.0',
                        id: requestId,
                        method: 'unsubscribeAddress',
                        params: [wallet, connectionId]
                    }));

                    // Timeout after 10 seconds
                    setTimeout(() => {
                        ws.removeEventListener('message', messageHandler);
                        resolve(false);
                    }, 10000);
                });
            });

            const results = await Promise.all(promises);
            return results.every(Boolean);
        } catch (error) {
            console.error('Error unsubscribing from trade wallets:', error);
            return false;
        }
    }
}));

// Initialize connection on module load with a longer delay and retry logic
setTimeout(() => {
    const { connect, setDebugMode } = useGRPCConnector.getState();

    // Enable debug mode in development
    if (process.env.NODE_ENV === 'development') {
        setDebugMode(true);
    }

    // Skip connection if disabled
    if (DISABLE_GRPC_CONNECTION) {
        return;
    }

    const connectWithRetry = (retries = 3) => {
        connect().catch(error => {
            console.error(`Initial connection failed (attempt ${4 - retries}): ${error}`);
            if (retries > 0) {
                console.log(`Retrying connection in ${4 - retries}s...`);
                setTimeout(() => connectWithRetry(retries - 1), (4 - retries) * 1000);
            }
        });
    };

    connectWithRetry();
}, 2000); // Increased from 1500ms to give the app more time to initialize
